"use client";

import * as React from "react";
import type { z } from "zod";

import type { bannerSchema } from "@/components/nav/types";
import { Container } from "@/components/ui/container";
import { navClearanceSpacerClass } from "@/lib/nav-layout";
import { cn } from "@/lib/utils";

import { Banner, useShowBanner } from "./banner";

interface HeaderProps {
  children: React.ReactNode;
  isColored: boolean | null;
  isNavOpen?: boolean;
  isDark?: boolean;
  /** Force light header bar while the mobile drawer is open. */
  isMobileNavOpen?: boolean;
  /** Transparent bar over a full-bleed hero (Figma Insights / VsNav pattern). */
  overlayAtTop?: boolean;
  banner: z.infer<typeof bannerSchema> | null;
}
export const Header = React.forwardRef<HTMLElement, HeaderProps>(
  (
    {
      children,
      banner,
      isDark = false,
      overlayAtTop = false,
      isMobileNavOpen = false,
    },
    ref
  ) => {
    const bannerProps = useShowBanner({
      enabled: Boolean(banner),
      bannerId: banner?._id ?? "",
      featureFlag: banner?.featureFlag,
    });
    const { showBanner, bannerHeight } = bannerProps;

    return (
      <>
        {banner && <Banner {...bannerProps} {...banner} />}
        <header
          ref={ref}
          style={{
            top: showBanner ? `${bannerHeight}px` : undefined,
          }}
          className={cn(
            "fixed top-[var(--announcement-h)] z-50 flex h-[68px] w-full items-center border-b px-6 py-5 lg:h-[72px] lg:px-12",
            "transition-[background-color,border-color,backdrop-filter] duration-200 ease-out motion-reduce:transition-none",
            isMobileNavOpen && "bg-grey-0 border-grey-100",
            !isMobileNavOpen &&
              overlayAtTop &&
              "border-white/20 bg-transparent",
            !isMobileNavOpen &&
              !overlayAtTop &&
              isDark &&
              "bg-grey-950 border-grey-900",
            !isMobileNavOpen &&
              !overlayAtTop &&
              !isDark &&
              "bg-grey-50 border-grey-100"
          )}
        >
          <Container>{children}</Container>
        </header>
      </>
    );
  }
);

Header.displayName = "Header";

export function HeaderSpacer({ className }: { className?: string }) {
  return (
    <div
      aria-hidden
      data-nav-offset-spacer
      className={cn(navClearanceSpacerClass, className)}
    />
  );
}
